import LocalForage from 'localforage';
import { StorageInterface } from './storage.util';

export const Drivers = {
    IndexedDB: LocalForage.INDEXEDDB,
    LocalStorage: LocalForage.LOCALSTORAGE
};

type Driver = any;

export interface LocalForageConfig extends LocalForageOptions {
    driverOrder?: Driver[];
}

const defaultConfig = {
    name: '__storage',
    storeName: '__storageKv',
    dbKey: '__storageKey',
    driverOrder: [
        Drivers.IndexedDB,
        Drivers.LocalStorage
    ]
};

export class LocalForageStorage implements StorageInterface<typeof LocalForage, LocalForageConfig>{
    #databaseInstance: Promise<typeof LocalForage>;

    constructor(public readonly config: LocalForageConfig) {
        this.config = { ...defaultConfig, ...config };
        this.#databaseInstance = this.create();
    }

    async create(): Promise<typeof LocalForage> {
        const db = LocalForage.createInstance(this.config);
        await db.setDriver(this.config.driverOrder || []);
        return db;
    }

    async get<T>(key: string): Promise<T | undefined> {
        const db = await this.#databaseInstance;
        return db.getItem(key).then(res => typeof res === 'undefined' ? res : res as any);
    }

    async set<T>(key: string, value: T): Promise<void> {
        const db = await this.#databaseInstance;
        return db.setItem(key, value).then(() => { });
    }

    async remove(key: string): Promise<boolean> {
        const db = await this.#databaseInstance;
        return db.removeItem(key).then(() => true);
    }

    async clear(): Promise<void> {
        const db = await this.#databaseInstance;
        const keys = await this.keys();

        const p = keys.map(async key => db.removeItem(key));
        await Promise.all(p);
        return;
    }

    async length(): Promise<number> {
        const db = await this.#databaseInstance;
        return db.length();
    }

    async keys(): Promise<string[]> {
        const db = await this.#databaseInstance;
        return db.keys();
    }

    async forEach<T>(iteratorCallback: (value: T, key: string, iterationNumber: number) => any): Promise<T | undefined> {
        const db = await this.#databaseInstance;
        return db.iterate(iteratorCallback);
    }
}